/**
 * File: src/components/admin-config-editor.js
 * Mô tả: Component trình biên tập cấu hình Admin tự động phân chia nhóm Tab & Sub-Tab chuẩn theo tên trường gốc của JSON.
 * Liên quan: src/components/admin-dynamic-json-tabs.js, src/services/admin-config-service.js
 */

import { createElement } from '../utils/dom.js';
import { saveNewConfigRevision, exportStaticConfigFile, resetToDefaultRevision } from '../services/admin-config-service.js';
import { renderDynamicJsonTabs } from './admin-dynamic-json-tabs.js';

export const renderAdminConfigEditor = (gameId, activeRevision, onSaveSuccess) => {
  const container = createElement('div', { className: 'admin-config-editor-container' });

  let currentConfig = JSON.parse(JSON.stringify(activeRevision.configData));
  let isRawJsonMode = false;

  const renderEditor = () => {
    container.innerHTML = '';
    const wrapper = createElement('div', {
      className: 'editor-card',
      attributes: { style: 'background: var(--bg-tertiary); padding: 1.5rem; border-radius: var(--radius-lg); border: var(--border-glass);' },
    });

    wrapper.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.25rem; flex-wrap: wrap; gap: 0.5rem;">
        <h3 style="font-size: 1.25rem;">🛠️ Trình Biên Tập Cấu Hình Nhiều Cấp: <span style="color: var(--color-accent); font-family: monospace;">${gameId}</span></h3>
        <span style="font-size: 0.85rem; color: var(--color-text-muted); font-weight: 600;">Phiên bản: v${activeRevision.version} (Rev #${activeRevision.revisionId})</span>
      </div>

      <div style="margin-bottom: 1rem; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.5rem;">
        <span style="font-size: 0.85rem; color: var(--color-text-muted);">Hiển thị nhóm Tab & Sub-Tab chuẩn tên trường JSON:</span>
        <button id="toggle-editor-mode" class="btn btn-secondary" style="padding: 6px 14px; font-size: 0.8rem;">
          ${isRawJsonMode ? '🎛️ Chuyển Sang Giao Diện Tab Đa Cấp' : '📜 Chuyển Sang Mã JSON Gốc'}
        </button>
      </div>

      <div id="editor-body-container" style="margin-bottom: 1.5rem;"></div>

      <div style="margin-bottom: 1rem;">
        <label style="display: block; font-size: 0.85rem; color: var(--color-text-muted); margin-bottom: 0.25rem;">Ghi chú thay đổi (Note)</label>
        <input type="text" id="input-save-note" placeholder="VD: Cập nhật chỉ số trong JSON config..." style="width: 100%; padding: 10px; border-radius: 6px; background: var(--bg-primary); border: var(--border-glass); color: #fff;" />
      </div>

      <div id="editor-alert-message" style="margin-bottom: 1rem; display: none; font-size: 0.9rem;"></div>

      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.75rem;">
        <button id="save-config-btn" class="btn btn-primary">
          <span>💾 Lưu & Xuất Bản Lên Server (Pages Function)</span>
        </button>
        <button id="reset-default-btn" class="btn btn-secondary" style="background: rgba(59, 130, 246, 0.2); border: 1px solid #3b82f6; color: #60a5fa;">
          <span>🔄 Nạp Cấu Hình v1.0.324 (Mã Nguồn)</span>
        </button>
        <button id="export-static-btn" class="btn btn-secondary" style="background: rgba(16, 185, 129, 0.2); border: 1px solid var(--color-success); color: var(--color-success);">
          <span>📥 Tải File Tĩnh (.bin & info.json)</span>
        </button>
      </div>
    `;

    const bodyContainer = wrapper.querySelector('#editor-body-container');
    const toggleBtn = wrapper.querySelector('#toggle-editor-mode');
    const saveBtn = wrapper.querySelector('#save-config-btn');
    const resetBtn = wrapper.querySelector('#reset-default-btn');
    const exportBtn = wrapper.querySelector('#export-static-btn');
    const alertEl = wrapper.querySelector('#editor-alert-message');

    if (isRawJsonMode) {
      bodyContainer.innerHTML = `
        <textarea id="json-textarea" style="width: 100%; height: 380px; font-family: monospace; font-size: 0.85rem; padding: 1rem; border-radius: 8px; background: #000; color: #10b981; border: var(--border-glass); outline: none;">${JSON.stringify(currentConfig, null, 2)}</textarea>
      `;
    } else {
      const dynamicTabs = renderDynamicJsonTabs(currentConfig, () => {});
      bodyContainer.appendChild(dynamicTabs);
    }

    toggleBtn.addEventListener('click', () => {
      isRawJsonMode = !isRawJsonMode;
      renderEditor();
    });

    saveBtn.addEventListener('click', async () => {
      if (isRawJsonMode) {
        const jsonArea = wrapper.querySelector('#json-textarea');
        try {
          currentConfig = JSON.parse(jsonArea.value);
        } catch (err) {
          alertEl.style.display = 'block';
          alertEl.style.color = 'var(--color-error)';
          alertEl.textContent = '❌ Lỗi cú pháp JSON. Vui lòng kiểm tra lại.';
          return;
        }
      } else {
        wrapper.querySelectorAll('.field-input, .table-arr-input, .simple-arr-input').forEach((el) => {
          el.dispatchEvent(new Event('change'));
        });
      }

      saveBtn.disabled = true;
      saveBtn.innerHTML = '<span>⏳ Đang xử lý lưu server...</span>';

      const note = wrapper.querySelector('#input-save-note').value.trim() || 'Cập nhật cấu hình game';
      const result = await saveNewConfigRevision(gameId, currentConfig, 'Admin', note);

      saveBtn.disabled = false;
      saveBtn.innerHTML = '<span>💾 Lưu & Xuất Bản Lên Server (Pages Function)</span>';

      alertEl.style.display = 'block';
      if (result.success) {
        alertEl.style.color = 'var(--color-success)';
        alertEl.textContent = `${result.message}`;
        if (onSaveSuccess) onSaveSuccess();
      } else {
        alertEl.style.color = 'var(--color-error)';
        alertEl.textContent = `❌ ${result.message} (${result.errors?.join(', ') || ''})`;
      }
    });

    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        resetToDefaultRevision(gameId);
        alertEl.style.display = 'block';
        alertEl.style.color = 'var(--color-success)';
        alertEl.textContent = '🔄 Đã nạp lại cấu hình mặc định v1.0.324 từ mã nguồn!';
        if (onSaveSuccess) onSaveSuccess();
      });
    }

    exportBtn.addEventListener('click', () => {
      exportStaticConfigFile(gameId);
      alertEl.style.display = 'block';
      alertEl.style.color = 'var(--color-success)';
      alertEl.textContent = '📦 Đã tải 2 file tĩnh game-config.bin và info.json!';
    });

    container.appendChild(wrapper);
  };

  renderEditor();
  return container;
};
